import type {MaterialNodeDefinition} from "#material/nodes/base/material/types/definition"
import {ExchangeNodeName} from "#material/exchange/node-name"
import {materialUiSchema} from "#material/nodes/base/material/validation/ui"
import type {MaterialNodeSockets} from "#material/types/material-node-sockets"
import {z} from "zod/v4-mini"
import {color} from "@cm/graph-v3"
import {vec3} from "#material/types/vector"
import {materialClosureOutput} from "#material/nodes/base/material/validation/io"
import {optionalMaterialInput} from "#material/nodes/base/connection/validation/parameters"
import type {Distribution} from "#material/nodes/shader/bsdf-glass/types"
import {distributionSchema} from "#material/nodes/shader/bsdf-glass/types"

export interface BsdfGlassSockets extends MaterialNodeSockets {
    inputs: {
        color: "color" // found in inputs: -; found in settings: color; inferred from data: float3
        ior: "float" // found in inputs: -; found in settings: float; inferred from data: float
        normal: "normal" // found in inputs: -; found in settings: vector; inferred from data: float3
        roughness: "float" // found in inputs: -; found in settings: float; inferred from data: float
    }
    settings: {
        color: "color" // found in inputs: -; found in settings: color; inferred from data: float3
        ior: "float" // found in inputs: -; found in settings: float; inferred from data: float
        normal: "vector" // found in inputs: -; found in settings: vector; inferred from data: float3
        roughness: "float" // found in inputs: -; found in settings: float; inferred from data: float
    }
    settingsExtensions: {
        distribution?: Distribution // found in inputs: -; found in settings: string; inferred from data: string
    }
    outputs: {
        bsdf: "closure"
    }
}

export const bsdfGlassDefinition = {
    exchangeFormat: {
        nodeName: ExchangeNodeName.ShaderNodeBsdfGlass,
    },
    nodeClass: "BsdfGlass",
    ui: {
        label: "Glass BSDF",
        color: "#2d8a5d",
        items: [
            {name: "bsdf", label: "BSDF", valueType: "closure", output: true},
            {name: "color", label: "Color", valueType: "color", input: true, setting: true, defaultValue: {r: 0.8, g: 0.8, b: 0.8}},
            {name: "ior", label: "IOR", valueType: "float", input: true, setting: true, defaultValue: 1.5, range: {min: 1, max: 2.5}},
            {name: "normal", label: "Normal", valueType: "vector", input: true, setting: true, defaultValue: {x: 0, y: 0, z: 0}},
            {name: "roughness", label: "Roughness", valueType: "float", input: true, setting: true, defaultValue: 0, range: {min: 0, max: 1}},
            {
                defaultValue: "GGX",
                name: "distribution",
                label: "Distribution",
                valueType: "string",
                options: [
                    {value: "BECKMANN", label: "Beckmann"},
                    {value: "GGX", label: "GGX"},
                    {value: "MULTI_GGX", label: "Multiscatter GGX"},
                ],
                input: false,
                setting: true,
            },
        ],
        support: {
            cycles: true,
            three: true,
        },
    },
    validation: {
        returns: z.strictObject({
            bsdf: materialClosureOutput,
        }),
        parameters: z.strictObject({
            inputs: z.strictObject({
                color: optionalMaterialInput,
                ior: optionalMaterialInput,
                normal: optionalMaterialInput,
                roughness: optionalMaterialInput,
            }),
            settings: z.strictObject({
                color: z.optional(color),
                distribution: z.optional(distributionSchema),
                roughness: z.optional(z.number()),
                ior: z.optional(z.number()),
                normal: z.optional(vec3),
                weight: z.optional(z.number()), // legacy parameter
            }),
            ui: materialUiSchema,
        }),
    },
} satisfies MaterialNodeDefinition<BsdfGlassSockets>
